import { API } from '../Auth';

const EP = 'discoverRuns';
const SUMMARY_PATH = '/api/v1/landscape/enterprise/summary';
const sourceDiffPath = (id) => `/api/v1/landscape/enterprise/sources/${encodeURIComponent(id)}/diff`;

export const SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'CLEAN', 'NEVER_SCANNED'];
export const DATA_CLASSES = ['STRUCTURED', 'SEMI_STRUCTURED', 'UNSTRUCTURED'];

const num = (v, dflt = 0) => (Number.isFinite(Number(v)) ? Number(v) : dflt);
const numOrNull = (v) => (v == null ? null : num(v));

const normalizeTotals = (t) => (t && typeof t === 'object'
  ? { resolved: num(t.resolved), persisting: num(t.persisting), new: num(t.new) }
  : null);

const normalizeRun = (r) => (r && typeof r === 'object' && r.job_id != null
  ? { jobId: Number(r.job_id), externalJobId: r.external_job_id || null, completedAt: r.completed_at || null }
  : null);

const normalizeSource = (s) => ({
  id: Number(s.id),
  name: s.name || '',
  datasourceType: s.datasource_type || 'RELATIONAL',
  vfsScheme: s.vfs_scheme || null,
  engine: s.engine || '',
  landscape: s.landscape || 'enterprise',
  dataClass: DATA_CLASSES.includes(s.data_class) ? s.data_class : 'UNSTRUCTURED',
  severity: SEVERITIES.includes(s.severity) ? s.severity : 'NEVER_SCANNED',
  findingTotal: num(s.finding_total),
  topEntities: Array.isArray(s.top_entities)
    ? s.top_entities.map((e) => ({ entity: e.entity || '', count: num(e.count) })) : [],
  latestRun: normalizeRun(s.latest_run),
  previousRun: normalizeRun(s.previous_run),
  diff: normalizeTotals(s.diff),
  openFindings: numOrNull(s.open_findings),
  diffError: s.diff_error || null,
});

/**
 * GET /api/v1/landscape/enterprise/summary — sources tagged landscape=enterprise|both with
 * severity, instance totals, last two completed runs and cached diff totals.
 *
 * Units: findingTotal/topEntities are INSTANCE counts (the severity scale and the entity chips
 * run on those); diff/openFindings/sinceLastScan are FIELDS — distinct columns, NoSQL fields or
 * files. Everything the home page RENDERS is in fields, which is why its tiles, its queue and
 * its strip are comparable and add up. A source with one completed run carries its baseline
 * (resolved = persisting = 0, every field new) rather than a null diff.
 */
export async function getEnterpriseSummary() {
  const res = await API.get(EP, SUMMARY_PATH);
  const payload = res?.data ? res.data : res;
  return {
    generatedAt: payload?.generated_at || new Date().toISOString(),
    sinceLastScan: normalizeTotals(payload?.since_last_scan) || { resolved: 0, persisting: 0, new: 0 },
    sources: Array.isArray(payload?.sources) ? payload.sources.map(normalizeSource) : [],
  };
}

const normalizeRow = (r) => ({
  status: r.status || 'PERSISTING',
  location: r.location || '',
  locationKind: r.location_kind || 'FIELD',
  entity: r.entity || '',
  countFrom: numOrNull(r.count_from),
  countTo: numOrNull(r.count_to),
  source: r.source || null,
  schema: r.schema || null,
  table: r.table || null,
  field: r.field || null,
  filePath: r.file_path || null,
});

/**
 * GET /api/v1/landscape/enterprise/sources/{id}/diff — run-to-run rows. `from`/`to` are platform
 * job ids; both omitted = the last two completed runs (or a baseline diff when only one exists).
 * `to` without `from` = baseline: the run is diffed against an empty run, so every row is NEW and
 * `fromJobId` comes back null. Throws (404) when the source has no completed run.
 */
export async function getEnterpriseSourceDiff(id, { from, to, status, limit, offset } = {}) {
  const params = {};
  if (from != null) params.from = from;
  if (to != null) params.to = to;
  if (status) params.status = status;
  if (limit != null) params.limit = limit;
  if (offset != null) params.offset = offset;
  const res = await API.get(EP, sourceDiffPath(id), { queryStringParameters: params });
  const p = res?.data ? res.data : res;
  return {
    fromJobId: p?.from_job_id != null ? Number(p.from_job_id) : null,
    toJobId: p?.to_job_id != null ? Number(p.to_job_id) : null,
    dataSourceId: p?.data_source_id != null ? Number(p.data_source_id) : null,
    fromCompletedAt: p?.from_completed_at || null,
    toCompletedAt: p?.to_completed_at || null,
    mode: p?.mode || 'EMPTY',
    totals: normalizeTotals(p?.totals) || { resolved: 0, persisting: 0, new: 0 },
    entityTotalsFrom: p?.entity_totals_from || {},
    entityTotalsTo: p?.entity_totals_to || {},
    rowCount: num(p?.row_count),
    more: num(p?.more),
    rows: Array.isArray(p?.rows) ? p.rows.map(normalizeRow) : [],
  };
}

/**
 * The platform answers 501 `DIFF_NOT_SUPPORTED` when the run being opened was executed by a
 * worker with no findings-diff route — a third-party integration adapter (Delphix et al.) serves
 * only part of the worker contract. That is a capability gap, not an outage, so the detail page
 * explains it instead of rendering a failure.
 */
export function isDiffUnsupportedError(err) {
  return err?.body?.code === 'DIFF_NOT_SUPPORTED' || err?.status === 501;
}

/** The worker's FindingsDiffService.MAX_LIMIT. Anything larger is clamped server-side. */
export const DIFF_MAX_LIMIT = 5000;

/** Safety cap on a client-side load-all. Past this the caller renders a "showing first N" banner. */
export const DIFF_LOAD_CAP = 50000;

/**
 * Load EVERY diff row for a source, following the `more` cursor.
 *
 * Paged at DIFF_MAX_LIMIT rather than the ledger's old 500 because the worker recomputes the
 * WHOLE diff on every call — loadRun(from) + loadRun(to) + computeDiff() + sort, then
 * `.subList(offset, end)` for the page (FindingsDiffService.diff). Pagination is presentation
 * only; there is no SQL-level offset. So a 5,000-row page costs the server exactly what a
 * 500-row page costs, and asking for 500 is simply 10x the round trips for no saving. At the
 * Facets reference scale (~15k-45k diff rows) this is 3-10 requests instead of 30-90.
 *
 * The FIRST response's `rowCount` is the exact unfiltered total, so `onProgress` can report a
 * real denominator from request #1 instead of an open-ended spinner.
 *
 * Resolves to the first page's envelope with `rows` replaced by every row loaded, plus
 * `truncated` — true when the cap stopped us short, so counts are understated and the UI must
 * say so rather than quietly under-report.
 */
export async function getAllEnterpriseSourceDiffPages(id, {
  from, to, status, maxRows = DIFF_LOAD_CAP, onProgress,
} = {}) {
  const first = await getEnterpriseSourceDiff(id, { from, to, status, limit: DIFF_MAX_LIMIT, offset: 0 });
  const rows = [...first.rows];
  if (typeof onProgress === 'function') onProgress(rows.length, first.rowCount);

  let more = first.more;
  while (more > 0 && rows.length < maxRows) {
    // eslint-disable-next-line no-await-in-loop -- offset depends on what has landed so far
    const next = await getEnterpriseSourceDiff(id, {
      from, to, status, limit: DIFF_MAX_LIMIT, offset: rows.length,
    });
    if (next.rows.length === 0) break; // defensive: never spin if the worker stops yielding rows
    rows.push(...next.rows);
    more = next.more;
    if (typeof onProgress === 'function') onProgress(rows.length, next.rowCount);
  }

  return { ...first, rows, more, truncated: more > 0 };
}
